// 표준 상품 규칙 — 브랜드 접기·별칭 풀기·바코드·링크·상태·연결 제안은 인메모리·Supabase·화면이 이 한 벌을 쓴다.

import type {
  CatalogAttrDef,
  CatalogAttrs,
  CatalogBrand,
  CatalogBrandAlias,
  CatalogLinkFilter,
  CatalogLinkStatus,
  CatalogProduct,
  CatalogSubmission,
} from "./types";

export const CATALOG_COLOR_KEY = "color";

// 브랜드·별칭 비교 열쇠 — 공백을 걷고 소문자로(SQL 유일 인덱스 lower(regexp_replace(name, '\s', '', 'g')) 와 같은 뜻).
export const foldBrand = (name: string): string => name.replace(/\s/g, "").toLowerCase();

// 모델 비교 열쇠 — 글자·숫자만 남기고 소문자로(「예시-T100」 = 「예시 t100」).
export const normalizeModel = (model: string): string => model.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");

// 이름·별칭 → 브랜드 — 그 조직이 볼 수 있는 브랜드(표준 + 우리 회사)만 받는다.
export const resolveBrand = (
  name: string,
  brands: readonly CatalogBrand[],
  aliases: readonly CatalogBrandAlias[]
): CatalogBrand | null => {
  const key = foldBrand(name);
  if (key === "") return null;
  const direct = brands.find((b) => foldBrand(b.name) === key);
  if (direct) return direct;
  const alias = aliases.find((a) => foldBrand(a.alias) === key);
  return alias ? (brands.find((b) => b.id === alias.brand_id) ?? null) : null;
};

// 입력 중 후보 — 이름·별칭 앞부분이 맞는 브랜드(별칭으로 맞으면 그 별칭을 같이 준다).
export const brandCandidates = (
  typed: string,
  brands: readonly CatalogBrand[],
  aliases: readonly CatalogBrandAlias[],
  limit = 6
): { brand: CatalogBrand; via: string | null }[] => {
  const key = foldBrand(typed);
  if (key === "") return [];
  const out = new Map<string, { brand: CatalogBrand; via: string | null }>();
  for (const b of brands) if (foldBrand(b.name).includes(key)) out.set(b.id, { brand: b, via: null });
  for (const a of aliases) {
    if (out.has(a.brand_id) || !foldBrand(a.alias).includes(key)) continue;
    const brand = brands.find((b) => b.id === a.brand_id);
    if (brand) out.set(brand.id, { brand, via: a.alias });
  }
  return [...out.values()].slice(0, limit);
};

// GTIN-13(EAN-13) — 숫자 13자리 + 마지막 자리 검사값.
export const isGtin13 = (code: string): boolean => {
  if (!/^\d{13}$/.test(code)) return false;
  const digits = code.split("").map(Number);
  const sum = digits.slice(0, 12).reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10 === digits[12];
};

// 상품 링크 — http(s) 주소만 저장한다(읽으러 가지 않는다).
export const isCatalogLink = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") && url.hostname !== "";
  } catch {
    return false;
  }
};

// 분류의 속성 정의로 값을 거른다 — 모르는 키·빈 값은 버리고 고를 값이 아니면 오류 문구.
export const parseCatalogAttrs = (
  defs: readonly CatalogAttrDef[],
  raw: Record<string, unknown>,
  rawColors: readonly unknown[]
): { ok: true; attrs: CatalogAttrs; colors: string[] } | { ok: false; message: string } => {
  const attrs: CatalogAttrs = {};
  let colors: string[] = [];
  for (const def of defs) {
    if (def.type === "pills") {
      const source = def.key === CATALOG_COLOR_KEY ? rawColors : Array.isArray(raw[def.key]) ? (raw[def.key] as unknown[]) : [];
      const picked = [...new Set(source.filter((v): v is string => typeof v === "string" && v.trim() !== "").map((v) => v.trim()))];
      const bad = def.options ? picked.find((v) => !def.options?.includes(v)) : undefined;
      if (bad !== undefined) return { ok: false, message: `${def.label}에 없는 값입니다 — ${bad}` };
      if (def.key === CATALOG_COLOR_KEY) colors = picked;
      else if (picked.length > 0) attrs[def.key] = picked.join(",");
      continue;
    }
    const value = raw[def.key];
    if (value === undefined || value === null || value === "") continue;
    if (def.type === "number") {
      const n = typeof value === "number" ? value : Number(String(value).trim());
      if (!Number.isFinite(n) || n < 0 || n > 1_000_000) return { ok: false, message: `${def.label}은 0 이상 숫자로 넣어 주세요` };
      attrs[def.key] = n;
      continue;
    }
    const text = String(value).trim().slice(0, 100);
    if (text === "") continue;
    if (def.type === "select" && def.options && !def.options.includes(text)) return { ok: false, message: `${def.label}에 없는 값입니다 — ${text}` };
    attrs[def.key] = text;
  }
  return { ok: true, attrs, colors };
};

export interface CatalogMatchContext {
  brands: readonly CatalogBrand[];
  aliases: readonly CatalogBrandAlias[];
  products: readonly CatalogProduct[];
}

// 표준 브랜드 id 로 — 별칭은 이미 브랜드 id 에 붙어 있으므로 같은 id 면 같은 브랜드.
const sameBrand = (a: string | null, b: string | null): boolean => a !== null && a === b;

// 품목명에서 브랜드 찾기 — 이름·별칭(2자 이상)을 접은 글자가 품목명 안에 있으면 그 브랜드 id.
const brandIdsInText = (text: string, ctx: CatalogMatchContext): Set<string> => {
  const folded = foldBrand(text);
  const ids = new Set<string>();
  for (const b of ctx.brands) if (foldBrand(b.name).length >= 2 && folded.includes(foldBrand(b.name))) ids.add(b.id);
  for (const a of ctx.aliases) if (foldBrand(a.alias).length >= 2 && folded.includes(foldBrand(a.alias))) ids.add(a.brand_id);
  return ids;
};

// 연결 제안 — 브랜드·모델 정보가 있으면 같은 브랜드 + 같은 모델, 없으면 품목명 안에 상품 모델이 있는가(긴 모델 우선).
export const suggestCatalogProduct = (
  price: { scope: string; item: string },
  info: { brand_id: string | null; model: string | null } | null,
  ctx: CatalogMatchContext
): CatalogProduct | null => {
  const withModel = ctx.products.filter((p) => p.model !== null && normalizeModel(p.model).length >= 3);
  if (info && info.brand_id !== null && info.model !== null && normalizeModel(info.model) !== "") {
    const model = normalizeModel(info.model);
    return withModel.find((p) => sameBrand(p.brand_id, info.brand_id) && normalizeModel(p.model ?? "") === model) ?? null;
  }
  const text = normalizeModel(`${price.scope} ${price.item}`);
  const brandIds = brandIdsInText(`${price.scope} ${price.item}`, ctx);
  const hits = withModel
    .filter((p) => text.includes(normalizeModel(p.model ?? "")))
    .filter((p) => brandIds.size === 0 || (p.brand_id !== null && brandIds.has(p.brand_id)))
    .sort((a, b) => normalizeModel(b.model ?? "").length - normalizeModel(a.model ?? "").length);
  return hits[0] ?? null;
};

export interface CatalogPriceState {
  status: CatalogLinkStatus;
  product: CatalogProduct | null;
}

// 단가 줄 한 줄의 상태 — 연결 > 검수 대기 > 품목만 > 연결 제안 > 미연결 · 제안은 마지막 반려 요청의 브랜드·모델을 먼저 본다.
export const catalogPriceState = (
  price: { id: string; scope: string; item: string; catalog_product_id?: string | null; catalog_opt_out?: boolean },
  submissions: readonly CatalogSubmission[],
  ctx: CatalogMatchContext
): CatalogPriceState => {
  if (price.catalog_product_id) {
    return { status: "LINKED", product: ctx.products.find((p) => p.id === price.catalog_product_id) ?? null };
  }
  const mine = submissions.filter((s) => s.price_item_id === price.id);
  if (mine.some((s) => s.status === "PENDING")) return { status: "PENDING", product: null };
  if (price.catalog_opt_out === true) return { status: "OPT_OUT", product: null };
  const last = [...mine].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const info = last && last.brand_mode === "BRAND" ? { brand_id: last.brand_id, model: last.model } : null;
  const product = suggestCatalogProduct(price, info, ctx);
  return product ? { status: "SUGGESTED", product } : { status: "UNLINKED", product: null };
};

// 「연결」 거르개 — 미연결은 연결 제안을 포함하고 품목만은 전체에서만 보인다.
export const matchesLinkFilter = (status: CatalogLinkStatus, filter: CatalogLinkFilter): boolean => {
  switch (filter) {
    case "all":
      return true;
    case "linked":
      return status === "LINKED";
    case "unlinked":
      return status === "UNLINKED" || status === "SUGGESTED";
    case "pending":
      return status === "PENDING";
  }
};

export const catalogLinkFilterOf = (value: string | undefined): CatalogLinkFilter =>
  value === "linked" || value === "unlinked" || value === "pending" ? value : "all";

export const categoryLabel = (path: readonly string[]): string => path.join(" › ");
